import React, { useState, useEffect } from 'react';
import { ThemeToggle } from './ThemeToggle';
import type { CommandItem } from './CommandPalette';
import { CommandPalette } from './CommandPalette';
import { Button } from '../ui/Button';
import { useWorks } from '../../api/hooks';
import { Info, LibraryBig, Menu, Search, ShieldCheck, X } from 'lucide-react';
import { brand } from '../../app/brand';
import { BrandLogo } from '../common/BrandLogo';
import { formatLegalTitle } from '../ui/formatLegalTitle';
import './Header.css';

export interface HeaderProps {
  onNavigate?: ((path: string) => void) | undefined;
  activePath?: string | undefined;
}

export const Header: React.FC<HeaderProps> = ({ onNavigate, activePath = '/' }) => {
  const [cmdOpen, setCmdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const worksQuery = useWorks();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navItems = [
    {
      label: 'Browse Corpus',
      detail: 'Statutory families',
      path: '/corpus',
      icon: LibraryBig,
    },
    { label: 'Search', detail: 'Find provisions', path: '/search', icon: Search },
    {
      label: 'About the data',
      detail: 'Quality & review',
      path: '/quality',
      icon: ShieldCheck,
    },
    { label: 'About', detail: 'About the project', path: '/about', icon: Info },
  ];

  const navigateTo = (path: string) => {
    setMobileOpen(false);
    onNavigate?.(path);
  };

  const defaultCommandItems: CommandItem[] = [
    {
      id: 'corpus',
      title: 'Browse Statutory Families',
      category: 'Navigation',
      onSelect: () => onNavigate?.('/corpus'),
    },
    {
      id: 'search',
      title: 'Semantic Search over Provisions',
      category: 'Navigation',
      onSelect: () => onNavigate?.('/search'),
    },
    {
      id: 'coverage',
      title: 'Data quality & research notes',
      category: 'Navigation',
      onSelect: () => onNavigate?.('/quality'),
    },
    {
      id: 'about',
      title: `About ${brand.fullName}`,
      category: 'Navigation',
      onSelect: () => onNavigate?.('/about'),
    },
  ];

  const workCommandItems: CommandItem[] = (worksQuery.data || []).map((w) => ({
    id: `work-${w.work_id}`,
    title: `${formatLegalTitle(w.short_title)} (${w.domain || 'Statute'})`,
    category: 'Statutory Family',
    onSelect: () => onNavigate?.(`/work/${w.work_id}`),
  }));

  const allCommandItems = [...defaultCommandItems, ...workCommandItems];

  return (
    <header className="sedlex-header">
      <div className="sedlex-header__left">
        <a
          href="/"
          className="sedlex-logo"
          onClick={(event) => {
            if (
              onNavigate &&
              !event.ctrlKey &&
              !event.metaKey &&
              !event.shiftKey &&
              !event.altKey &&
              event.button === 0
            ) {
              event.preventDefault();
              navigateTo('/');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.preventDefault();
              navigateTo('/');
            }
          }}
          aria-label={`${brand.fullName} — home`}
          title={brand.fullName}
          tabIndex={0}
        >
          <BrandLogo decorative />
        </a>

        <nav className={`sedlex-nav ${mobileOpen ? 'is-open' : ''}`} aria-label="Primary">
          {navItems.map((it) => (
            <a
              key={it.path}
              href={it.path}
              aria-current={activePath === it.path ? 'page' : undefined}
              className={`sedlex-nav__link ${
                activePath === it.path ||
                (it.path === '/corpus' && activePath.startsWith('/work/'))
                  ? 'is-active'
                  : ''
              }`}
              onClick={(event) => {
                if (
                  onNavigate &&
                  !event.ctrlKey &&
                  !event.metaKey &&
                  !event.shiftKey &&
                  !event.altKey &&
                  event.button === 0
                ) {
                  event.preventDefault();
                  navigateTo(it.path);
                }
              }}
            >
              <it.icon aria-hidden="true" />
              <span>
                <b>{it.label}</b>
                <small>{it.detail}</small>
              </span>
            </a>
          ))}
        </nav>
      </div>

      <div className="sedlex-header__right">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setCmdOpen(true)}
          className="sedlex-cmd-trigger"
          aria-label="Quick search"
        >
          <Search aria-hidden="true" />
          <span className="sedlex-cmd-label">Quick find…</span>
          <kbd className="sedlex-kbd">Ctrl K</kbd>
        </Button>

        <ThemeToggle />
        <Button
          variant="ghost"
          size="sm"
          className="sedlex-mobile-toggle"
          aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </Button>
      </div>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} items={allCommandItems} />
    </header>
  );
};
